import React, { useEffect, useMemo, useRef, useState } from 'react';
import { APP_STORE_FILE } from '../../preview-bridge/dataRecordPatch';
import { appDataStore, LIVE_DATA_NOTICE } from '../../stores/appData';
import { useStore } from '@nanostores/react';
import { Button, cn } from '@ideoz/ui';
import { ArrowDown, ArrowUp, Copy, Plus, Trash2 } from 'lucide-react';
import { hotEditStore } from '../../stores/hotEdit';
import { pendingEdits, queueEdit } from '../../preview-bridge/pendingEdits';
import { GLASS, SURFACE } from './surface';
import { workbenchStore } from '../../stores/workbench';
import { getMergedFileContent } from '../../utils/mergedFiles';
import {
  addRecord,
  deleteRecord,
  duplicateRecord,
  findListData,
  moveRecord,
  setCell,
  type ListData,
} from '../../preview-bridge/listDataFile';

/**
 * The list's data as a sheet the founder pulls up over the preview (hot edit plan, phase 3;
 * Prototype Editor UX plan, step 6). One row per record, one column per field; edits, add,
 * duplicate, delete and reorder write the data file as hot edits (undo works). Clicking a row
 * selects its card in the preview, and the selected card's row is highlighted.
 *
 * The sheet is dragged to the height the founder wants, between a few rows and most of the
 * preview, and remembered for the session; Done or Escape puts it away and keeps the selection.
 */
export function ListDataGrid(): React.ReactElement | null {
  const grid = useStore(hotEditStore.dataGrid);
  const files = useStore(workbenchStore.files);
  const selection = useStore(hotEditStore.selection);
  const api = useStore(hotEditStore.bridgeApi);
  const isStreaming = useStore(workbenchStore.isStreaming);
  const pending = useStore(pendingEdits);
  const [error, setError] = useState<string | null>(null);

  // A pending data change is the newest copy of that file, so the next edit is computed from it
  // rather than from what is still on disk.
  const readFile = useMemo(() => {
    const queued = new Map<string, string>();
    for (const edit of pending) if (edit.kind === 'data') queued.set(edit.filePath, edit.content);
    return (path: string) => queued.get(path) ?? getMergedFileContent(path);
  }, [pending]);

  // Selecting an element that is not a row of this list puts the sheet away: it belongs to the
  // list the founder opened it on, not to the page.
  const openRecordId = grid?.recordId ?? null;
  const selectedRecordId = selection?.listContext?.reactKey ?? selection?.listRow?.key ?? null;
  useEffect(() => {
    if (!openRecordId || !selection) return;
    if (selectedRecordId == null) hotEditStore.dataGrid.set(null);
  }, [openRecordId, selection, selectedRecordId]);

  const list = useMemo<ListData | null>(() => {
    if (!grid) return null;
    return findListData({
      recordId: grid.recordId,
      filePaths: Object.keys(files),
      readFile,
    });
  }, [grid, files, readFile]);

  if (!grid) return null;
  const close = () => hotEditStore.dataGrid.set(null);

  if (!list) {
    // On the shared data layer a list is the startup's live data, edited in the Data view.
    const live = Object.keys(files).some((p) => p.replace(/^\/+/, '') === APP_STORE_FILE);
    return (
      <GridShell title="List data" onClose={close} subtitle="">
        {live ? (
          <div className="flex items-center justify-between gap-3 px-3 py-3">
            <p className="text-xs text-muted-foreground">
              This list is your startup's live data. {LIVE_DATA_NOTICE}
            </p>
            <button
              type="button"
              className="shrink-0 rounded border border-border px-2 py-1 text-xs hover:bg-muted"
              onClick={() => {
                close();
                appDataStore.openDataView.set(appDataStore.openDataView.get() + 1);
              }}
            >
              Open in Data view
            </button>
          </div>
        ) : (
          <p className="px-3 py-3 text-xs text-muted-foreground">
            This list's data wasn't found in src/data. Ask Ideoz to change it instead.
          </p>
        )}
      </GridShell>
    );
  }

  const selectedKey = selection?.listContext?.reactKey ?? null;
  /**
   * The change joins the pending list instead of writing the file. Writing each cell on the spot
   * recompiled the preview, which reloaded it and dropped the founder out of whatever they were
   * editing mid-sentence (UI pass, issue 3).
   */
  const write = async (content: string | null, selectId?: string) => {
    if (content == null) {
      setError("That value doesn't fit this column's type.");
      return;
    }
    setError(null);
    if (isStreaming) return;
    // The file is what is being changed, so the change is kept whether or not something is
    // still picked in the preview - deleting the selected row used to throw the next one away.
    pendingEdits.set(
      queueEdit(pendingEdits.get(), {
        kind: 'data',
        elementId: selection?.elementId ?? `list:${list.filePath}`,
        selection: selection ?? null,
        filePath: list.filePath,
        content,
        label: `Data in ${list.arrayName}`,
      }),
    );
    hotEditStore.save.set('pending');
    if (selectId) hotEditStore.dataGrid.set({ recordId: selectId });
  };

  const editable = list.columns.filter((c) => c.name !== 'id');

  const selectedIndex = list.records.findIndex((r) => r.id === selectedKey);
  return (
    <GridShell
      title={list.arrayName}
      subtitle={`${list.records.length} items${selectedIndex >= 0 ? ` · editing item ${selectedIndex + 1}` : ''}`}
      onClose={close}
      actions={
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1"
          disabled={isStreaming}
          onClick={() => {
            const r = addRecord(list);
            if (!r) return;
            const last = list.records[list.records.length - 1];
            if (last) {
              api?.applyInstantRow({
                op: 'add',
                rowKey: last.id,
                newKey: r.id,
                values: last.fields
                  .filter((f) => f.name !== 'id' && f.kind !== 'other')
                  .map((f) => ({ from: String(f.value ?? ''), to: blankFor(f.kind) })),
              });
            }
            void write(r.content, r.id);
          }}
        >
          <Plus size={13} aria-hidden /> Add row
        </Button>
      }
    >
      {error ? <p className="px-3 py-1 text-[11px] text-red-700 bg-red-50">{error}</p> : null}
      <div className="overflow-auto flex-1 min-h-0">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0" style={{ background: 'var(--editor-glass-solid)' }}>
            <tr>
              {editable.map((c) => (
                <th key={c.name} scope="col" className="px-2 py-1.5 text-left font-medium whitespace-nowrap" style={{ color: 'var(--editor-ink-2)' }}>
                  {c.name}
                </th>
              ))}
              <th scope="col" className="px-2 py-1.5 w-[1%]">
                <span className="sr-only">Row actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.records.map((rec, i) => (
              <tr
                key={rec.id}
                className={cn(
                  'border-t border-border/50',
                  rec.id === selectedKey ? 'bg-black/[0.06] dark:bg-white/[0.08]' : 'hover:bg-black/[0.03] dark:hover:bg-white/[0.04]',
                )}
                onClick={() => api?.selectByKey(rec.id)}
              >
                {editable.map((c, ci) => {
                  const f = rec.fields.find((x) => x.name === c.name);
                  if (!f || f.kind === 'other') {
                    return (
                      <td key={c.name} className="px-2 py-1 text-muted-foreground" title="Not plain text: ask Ideoz to change it">
                        {f ? '(computed)' : ''}
                      </td>
                    );
                  }
                  return (
                    <td key={c.name} className="px-1 py-0.5">
                      {f.kind === 'boolean' ? (
                        <input
                          type="checkbox"
                          aria-label={`${c.name} of ${rec.id}`}
                          checked={f.value === true}
                          disabled={isStreaming}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => {
                            api?.applyInstantData(rec.id, String(f.value), String(e.target.checked), ci);
                            void write(setCell(list, rec.id, c.name, String(e.target.checked)));
                          }}
                        />
                      ) : (
                        <CellInput
                          key={`${rec.id}-${c.name}-${String(f.value)}`}
                          label={`${c.name} of ${rec.id}`}
                          numeric={f.kind === 'number'}
                          initial={String(f.value ?? '')}
                          disabled={isStreaming}
                          onCommit={(v) => {
                            // Shown in the preview as it is typed, saved with the rest on Apply:
                            // the file it comes from is only compiled then, so until then the
                            // words on the page are swapped for the words being edited.
                            api?.applyInstantData(rec.id, String(f.value ?? ''), v, ci);
                            void write(setCell(list, rec.id, c.name, v));
                          }}
                        />
                      )}
                    </td>
                  );
                })}
                <td className="px-1 py-0.5 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <RowButton
                    label="Move up"
                    disabled={i === 0 || isStreaming}
                    onClick={() => {
                      api?.applyInstantRow({ op: 'move', rowKey: rec.id, toIndex: i - 1 });
                      void write(moveRecord(list, rec.id, i - 1));
                    }}
                  >
                    <ArrowUp size={12} />
                  </RowButton>
                  <RowButton
                    label="Move down"
                    disabled={i === list.records.length - 1 || isStreaming}
                    onClick={() => {
                      api?.applyInstantRow({ op: 'move', rowKey: rec.id, toIndex: i + 1 });
                      void write(moveRecord(list, rec.id, i + 1));
                    }}
                  >
                    <ArrowDown size={12} />
                  </RowButton>
                  <RowButton
                    label="Duplicate"
                    disabled={isStreaming}
                    onClick={() => {
                      const r = duplicateRecord(list, rec.id);
                      if (!r) return;
                      api?.applyInstantRow({ op: 'duplicate', rowKey: rec.id, newKey: r.id });
                      void write(r.content, r.id);
                    }}
                  >
                    <Copy size={12} />
                  </RowButton>
                  <RowButton
                    label="Delete"
                    disabled={list.records.length <= 1 || isStreaming}
                    onClick={() => {
                      const next = list.records[i + 1] ?? list.records[i - 1];
                      api?.applyInstantRow({ op: 'remove', rowKey: rec.id });
                      void write(deleteRecord(list, rec.id), next?.id);
                    }}
                  >
                    <Trash2 size={12} />
                  </RowButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </GridShell>
  );
}

/** What a new record shows before the founder fills it in - the values `addRecord` writes. */
function blankFor(kind: string): string {
  if (kind === 'number') return '0';
  if (kind === 'boolean') return 'false';
  return '';
}

const SHEET_MIN = 180;
const SHEET_MAX_SHARE = 0.7;

function GridShell({
  title,
  subtitle,
  onClose,
  actions,
  children,
}: {
  title: string;
  subtitle: string;
  onClose: () => void;
  actions?: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  const height = useStore(hotEditStore.sheetHeight);
  const sheetRef = useRef<HTMLElement>(null);
  const drag = useRef<{ startY: number; startHeight: number } | null>(null);

  /** The tallest the sheet may be: most of the space it sits in, never all of it. */
  const maxHeight = () => {
    const parent = sheetRef.current?.parentElement;
    return parent ? Math.max(SHEET_MIN, Math.round(parent.clientHeight * SHEET_MAX_SHARE)) : 600;
  };
  const setHeight = (px: number) => hotEditStore.sheetHeight.set(Math.min(maxHeight(), Math.max(SHEET_MIN, Math.round(px))));

  const onGrab = (ev: React.PointerEvent) => {
    drag.current = { startY: ev.clientY, startHeight: sheetRef.current?.offsetHeight ?? height };
    (ev.target as HTMLElement).setPointerCapture(ev.pointerId);
  };
  const onMove = (ev: React.PointerEvent) => {
    if (!drag.current) return;
    setHeight(drag.current.startHeight + (drag.current.startY - ev.clientY));
  };
  const onRelease = () => {
    drag.current = null;
  };

  // Escape puts the sheet away; the selection stays. So does clicking anywhere outside it -
  // the sheet used to stay open behind whatever the founder went on to do (UI pass, issue 2).
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose();
    };
    const onPointerDown = (ev: PointerEvent) => {
      if (sheetRef.current?.contains(ev.target as Node)) return;
      // The preview is an iframe, so a click inside it never reaches here; selecting another
      // element closes the sheet through `closeWhenSelectionLeaves` below.
      onClose();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [onClose]);

  return (
    <section
      ref={sheetRef}
      aria-label="List data"
      className={cn(GLASS, SURFACE, 'absolute inset-x-4 bottom-0 z-20 flex flex-col rounded-t-2xl')}
      style={{ height, maxHeight: `${SHEET_MAX_SHARE * 100}%` }}
      data-testid="hot-edit-data-grid"
    >
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Drag to resize"
        title="Drag to resize. Double-click for tallest or shortest."
        className="flex h-4 shrink-0 cursor-row-resize items-center justify-center touch-none"
        onPointerDown={onGrab}
        onPointerMove={onMove}
        onPointerUp={onRelease}
        onPointerCancel={onRelease}
        onDoubleClick={() => setHeight(height >= maxHeight() - 4 ? SHEET_MIN : maxHeight())}
      >
        <span className="h-1 w-9 rounded-full bg-foreground/20" aria-hidden />
      </div>
      <div className="flex shrink-0 items-center gap-2 border-b border-border/60 px-3 pb-1.5">
        <span className="truncate text-xs font-semibold">{title}</span>
        {subtitle ? <span className="truncate text-[11px]" style={{ color: 'var(--editor-ink-2)' }}>{subtitle}</span> : null}
        <span className="flex-1" />
        {actions}
        <Button type="button" size="sm" className="h-7 px-3 text-xs" onClick={onClose} data-testid="hot-edit-data-grid-done">
          Done
        </Button>
      </div>
      {children}
    </section>
  );
}

function RowButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-30"
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/** Commits on blur or Enter; Escape restores the saved value. */
function CellInput({
  label,
  initial,
  numeric,
  disabled,
  onCommit,
}: {
  label: string;
  initial: string;
  numeric: boolean;
  disabled?: boolean;
  onCommit: (value: string) => void;
}): React.ReactElement {
  const [value, setValue] = useState(initial);
  return (
    <input
      aria-label={label}
      inputMode={numeric ? 'decimal' : undefined}
      className={cn(
        'w-full min-w-[80px] rounded border border-transparent bg-transparent px-1 py-0.5 text-xs focus:border-border focus:bg-background focus:outline-none',
        numeric && 'text-right tabular-nums',
      )}
      value={value}
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') setValue(initial);
      }}
      onBlur={() => {
        if (value !== initial) onCommit(value);
      }}
    />
  );
}
